/**
 * Domain Pack types for TypeScript
 * Maps to backend Pydantic models
 */

export type DomainPackStatus =
  | 'pending'
  | 'researching'
  | 'quality_review'
  | 'awaiting_approval'
  | 'published'
  | 'archived';

export type ResearchPhase =
  | 'phase_1_discovery'
  | 'phase_2_trends'
  | 'phase_3_issues'
  | 'phase_4_practices'
  | 'phase_5_data_elements'
  | 'phase_6_hypotheses'
  | 'completed';

export type HypothesisType =
  | 'root_cause'
  | 'performance'
  | 'process_efficiency'
  | 'cost_optimization'
  | 'risk_mitigation'
  | 'growth_opportunity';

export type HypothesisStatus =
  | 'pending'
  | 'testing'
  | 'validated'
  | 'rejected'
  | 'inconclusive';

export type IssueSeverity = 'critical' | 'high' | 'medium' | 'low';

export type PracticeComplexity = 'low' | 'medium' | 'high' | 'very_high';

// Summary view for listings
export interface DomainPackSummary {
  domain_pack_id: string;
  domain_name: string;
  description: string;
  industry_context?: string;
  status: DomainPackStatus;
  quality_score: number;
  subdomain_count: number;
  created_at: string;
  updated_at: string;
}

// Trend entity
export interface Trend {
  trend_id: string;
  title: string;
  description: string;
  impact: string;
  time_horizon: string; // 'short-term' | 'medium-term' | 'long-term'
  sources: string[];
  relevance_score: number;
  created_at: string;
}

// Issue entity
export interface Issue {
  issue_id: string;
  title: string;
  description: string;
  severity: IssueSeverity;
  root_causes: string[];
  symptoms: string[];
  business_impact: string;
  typical_context: string;
  created_at: string;
}

// Best Practice entity
export interface BestPractice {
  practice_id: string;
  title: string;
  description: string;
  implementation_steps: string[];
  expected_benefits: string[];
  prerequisites: string[];
  complexity: PracticeComplexity;
  estimated_effort: string;
  success_metrics: string[];
  sources: string[];
  created_at: string;
}

// Data Element entity
export interface DataElement {
  element_id: string;
  name: string;
  description: string;
  data_type: string;
  source_system?: string;
  required_for: string[];
  criticality: string; // 'essential' | 'important' | 'nice-to-have'
  typical_granularity: string;
  created_at: string;
}

// Hypothesis entity (template)
export interface Hypothesis {
  hypothesis_id: string;
  title: string;
  statement: string;
  hypothesis_type: HypothesisType;
  rationale: string;
  required_data: string[];
  test_approach: string;
  validation_criteria: string[];
  expected_impact: string;
  typical_success_rate?: number;
  validation_count: number;
  total_tests: number;
  created_at: string;
}

// Case Hypothesis (working copy)
export interface CaseHypothesis extends Hypothesis {
  case_id: string;
  source_hypothesis_id: string;
  source_domain_pack: string;
  source_subdomain: string;
  status: HypothesisStatus;
  priority: number;
  assigned_to?: string;
  test_results?: Record<string, any>;
  confidence?: number;
  findings?: string;
  action_items: string[];
  customized: boolean;
  original_statement?: string;
  customization_notes?: string;
  updated_at: string;
  tested_at?: string;
}

// Subdomain entity
export interface Subdomain {
  subdomain_id: string;
  name: string;
  description: string;
  scope: string;
  typical_kpis: string[];
  trends: Trend[];
  issues: Issue[];
  practices: BestPractice[];
  data_elements: DataElement[];
  hypotheses: Hypothesis[];
  quality_score: number;
  created_at: string;
  updated_at: string;
}

// Domain Pack (detailed view)
export interface DomainPackDetail {
  domain_pack_id: string;
  domain_name: string;
  description: string;
  industry_context?: string;
  status: DomainPackStatus;
  current_phase: ResearchPhase;
  quality_score: number;
  subdomains: Subdomain[];
  research_metadata: Record<string, any>;
  llm_provider?: string;
  llm_model?: string;
  total_tokens?: number;
  cost_estimate?: number;
  created_by?: string;
  reviewed_by?: string;
  approved_by?: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  // Statistics
  trend_count: number;
  issue_count: number;
  practice_count: number;
  data_element_count: number;
  hypothesis_count: number;
  case_application_count: number;
}

// Research Progress
export interface ResearchProgress {
  job_id: string;
  domain_pack_id: string;
  current_phase: ResearchPhase;
  phase_progress: number; // 0-1
  overall_progress: number; // 0-1
  current_activity: string;
  estimated_completion?: string;
  errors: string[];
}

// API Request types
export interface GenerateDomainPackRequest {
  domain_name: string;
  description?: string;
  industry_context?: string;
  selected_subdomains?: string[];
  force_regenerate?: boolean;
}

export interface GenerateDomainPackResponse {
  job_id: string;
  domain_pack_id: string;
  status: string;
  message: string;
}

export interface ApplyDomainPackRequest {
  case_id: string;
  domain_pack_id: string;
  selected_subdomains: string[];
  selected_hypotheses?: string[];
  applied_by: string;
}

export interface ApplyDomainPackResponse {
  case_id: string;
  domain_pack_id: string;
  hypotheses_copied: number;
  data_elements_linked: number;
  issues_flagged: number;
  practices_referenced: number;
  application_timestamp: string;
}

export interface UpdateHypothesisTestRequest {
  status: HypothesisStatus;
  confidence?: number;
  findings?: string;
  test_results?: Record<string, any>;
  action_items?: string[];
}

// UI-specific types
export interface DomainPackFilters {
  status?: DomainPackStatus;
  search?: string;
  sortBy?: 'name' | 'quality' | 'created' | 'updated';
  sortOrder?: 'asc' | 'desc';
}

export interface DomainPackStatistics {
  domain_pack_id: string;
  domain_name: string;
  statistics: {
    subdomains: number;
    trends: number;
    issues: number;
    best_practices: number;
    data_elements: number;
    hypotheses: number;
  };
  quality_score: number;
  status: DomainPackStatus;
}

// Phase display names
export const PHASE_NAMES: Record<ResearchPhase, string> = {
  phase_1_discovery: 'Domain Discovery',
  phase_2_trends: 'Trends Research',
  phase_3_issues: 'Issues Analysis',
  phase_4_practices: 'Best Practices',
  phase_5_data_elements: 'Data Elements',
  phase_6_hypotheses: 'Hypotheses Generation',
  completed: 'Complete',
};

// Status display names
export const STATUS_NAMES: Record<DomainPackStatus, string> = {
  pending: 'Pending',
  researching: 'Researching',
  quality_review: 'Quality Review',
  awaiting_approval: 'Awaiting Approval',
  published: 'Published',
  archived: 'Archived',
};

// Status colors for badges
export const STATUS_COLORS: Record<DomainPackStatus, string> = {
  pending: 'bg-neutral-200 text-neutral-700',
  researching: 'bg-blue-100 text-blue-700',
  quality_review: 'bg-yellow-100 text-yellow-700',
  awaiting_approval: 'bg-purple-100 text-purple-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-neutral-300 text-neutral-600',
};

// Severity colors
export const SEVERITY_COLORS: Record<IssueSeverity, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200',
};

// Complexity colors
export const COMPLEXITY_COLORS: Record<PracticeComplexity, string> = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  very_high: 'bg-red-100 text-red-700',
};

// Hypothesis status colors
export const HYPOTHESIS_STATUS_COLORS: Record<HypothesisStatus, string> = {
  pending: 'bg-neutral-200 text-neutral-700',
  testing: 'bg-blue-100 text-blue-700',
  validated: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  inconclusive: 'bg-yellow-100 text-yellow-700',
};
